import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  X, Calendar, ChevronRight, ChevronDown,
  ChevronFirst, ChevronLeft, ChevronLast, ListFilter,
} from 'lucide-react';
import ActionMenu from '../../components/common/ActionMenu';
import DownloadModal from '../../components/common/DownloadModal';
import { adminAuditApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import SearchClearIcon from '../../components/common/SearchClearIcon';
import AdvancedSearchModal from '../../components/common/AdvancedSearchModal';
import { canonicalQuery, SEARCH_HINT } from '../../utils/listSearch';
import StuiSelect from '../../components/common/StuiSelect';
import StuiDateTime from '../../components/common/StuiDateTime';
import { formatTimestamp, localDaysToUtcRange } from '../../utils/dateTime';

// What search reads (AdminAuditController keys), labelled for field: prefixes and the Advanced
// Search builder. Requests carry the canonical, key-based form (canonicalQuery).
const SEARCH_FIELD_LABELS = [
  { key: 'changedBy', label: 'User' }, { key: 'tableName', label: 'Table' },
  { key: 'operation', label: 'Operation' }, { key: 'recordIdentifier', label: 'Record' },
  { key: 'fieldName', label: 'Field' }, { key: 'oldValue', label: 'Old Value' },
  { key: 'newValue', label: 'New Value' }, { key: 'category', label: 'Category' },
  { key: 'action', label: 'Action' },
];
const SEARCH_FIELDS = SEARCH_FIELD_LABELS.map(f => f.key);

/**
 * Admin-database audit trail (DevTrack 127). SysAdmin only.
 *
 * The unit of display is a BATCH, not a row. One HTTP request is one user action,
 * and a single action can emit dozens of rows — AdminManagementController's
 * "copy user permissions" saves five times and can write ~25. Listing raw rows
 * would make one deliberate act look like a rampage, so batches collapse by
 * default and expand to their field rows on demand: git log, then git diff.
 *
 * Companion to AuditTrailCrudPage (project-database CRUD). No purge control here
 * — retention is DevTrack 125's call, so nothing in this UI deletes history.
 */

const DEFAULT_PAGE_SIZE = 25;

/**
 * Colours live in STUI.css as .stui-badge-audit-* — shared with AuditTrailCrudPage
 * so the two audit pages cannot drift. An unrecognised value yields a class with no
 * colour rule and falls back to the uncoloured base badge.
 */
const auditBadge = (value) =>
  `stui-badge-audit stui-badge-audit-${String(value || '').toLowerCase()}`;

// Flat shape used for the CSV download — one line per field row.
const DOWNLOAD_COLUMNS = [
  { key: 'changedAt',        label: 'Date',       width: 188 },
  { key: 'changedBy',        label: 'User',       width: 200 },
  { key: 'operation',        label: 'Operation',  width: 220 },
  { key: 'category',         label: 'Category',   width: 100 },
  { key: 'tableName',        label: 'Table',      width: 180 },
  { key: 'recordPk',         label: 'ID',         width: 60  },
  { key: 'recordIdentifier', label: 'Identifier', width: 200 },
  { key: 'action',           label: 'Action',     width: 80  },
  { key: 'fieldName',        label: 'Field',      width: 160 },
  { key: 'oldValue',         label: 'Old Value',  width: 320 },
  { key: 'newValue',         label: 'New Value',  width: 320 },
];

const AuditTrailAdminPage = () => {
  const { notify } = useNotifications();

  // ── Purge (DevTrack 135) ──────────────────────────────────────────────────
  // AudTrl_AdminCRUD had no retention at all. The policy is read-only here and lives on
  // Preference Defaults; this modal shows it, previews what would go, and runs it.
  const [showPurge, setShowPurge]         = useState(false);
  const [purgePreview, setPurgePreview]   = useState(null);
  const [previewing, setPreviewing]       = useState(false);
  const [purging, setPurging]             = useState(false);
  const [useOverride, setUseOverride]     = useState(false);
  const [overrideDays, setOverrideDays]   = useState('');
  // Records WHICH override the preview belongs to, so changing it re-locks Purge and nobody
  // deletes on the strength of a count for different numbers.
  const [previewedFor, setPreviewedFor]   = useState(null);

  const overrideParam = () => (useOverride && overrideDays !== '' ? parseInt(overrideDays, 10) : undefined);
  const previewKey    = () => (useOverride ? `o:${overrideDays}` : 'policy');

  const runPurgePreview = async () => {
    if (useOverride && (overrideDays === '' || isNaN(parseInt(overrideDays, 10)))) {
      notify.error('Enter a number of days, or untick the override.');
      return;
    }
    setPreviewing(true);
    try {
      const res = await adminAuditApi.purgeTrailPreview(overrideParam());
      setPurgePreview(res.data);
      setPreviewedFor(previewKey());
    } catch (err) {
      setPurgePreview(null);
      setPreviewedFor(null);
      notify.error(err?.response?.data?.error || 'Preview failed.');
    } finally {
      setPreviewing(false);
    }
  };

  const closePurge = () => {
    setShowPurge(false);
    setPurgePreview(null);
    setPreviewedFor(null);
    setUseOverride(false);
    setOverrideDays('');
  };

  const doPurge = () => {
    if (previewedFor !== previewKey()) { notify.error('Preview the purge before running it.'); return; }
    const p = purgePreview || {};
    notify.confirm(
      `Permanently delete ${p.totalRows} admin audit record(s)? There is no undo.`,
      async () => {
        setPurging(true);
        try {
          const res = await adminAuditApi.purgeTrail(overrideParam());
          notify.success(`Purged ${res.data.totalRows} record(s).`);
          closePurge();
          loadData(1);
        } catch (err) {
          notify.error(err?.response?.data?.error || 'Purge failed.');
        } finally {
          setPurging(false);
        }
      }
    );
  };

  const [batches, setBatches]       = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage]             = useState(1);
  const [pageSize, setPageSize]     = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading]       = useState(false);

  // batchId -> { loading, rows } — fetched lazily on first expand and cached,
  // so collapsing and re-expanding does not re-hit the API.
  const [expanded, setExpanded]     = useState({});


  const [searchTerm, setSearchTerm]       = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [showAdvSearch, setShowAdvSearch] = useState(false);

  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateFrom, setDateFrom]             = useState('');
  const [dateTo, setDateTo]                 = useState('');
  const [appliedFrom, setAppliedFrom]       = useState('');
  const [appliedTo, setAppliedTo]           = useState('');
  const dateFilterRef                       = useRef(null);

  const [showDownload, setShowDownload]           = useState(false);
  const [downloadRows, setDownloadRows]           = useState([]);
  const [preparingDownload, setPreparingDownload] = useState(false);

  const totalPages        = Math.max(1, Math.ceil(totalCount / pageSize));
  const dateFilterActive  = !!(appliedFrom || appliedTo);
  const filtersActive     = !!(appliedSearch || dateFilterActive);

  const loadData = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, pageSize };
      if (appliedSearch) params.search   = canonicalQuery(appliedSearch, SEARCH_FIELDS, SEARCH_FIELD_LABELS);
      const range = localDaysToUtcRange(appliedFrom, appliedTo);
      if (range.from) params.dateFrom = range.from;
      if (range.to)   params.dateTo   = range.to;

      const res = await adminAuditApi.getBatches(params);
      setBatches(res.data?.batches || []);
      setTotalCount(res.data?.totalCount || 0);
      setPage(res.data?.page || pg);
      setExpanded({});   // page changed — stale expansions would be confusing
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to load the admin audit trail');
      setBatches([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [pageSize, appliedSearch, appliedFrom, appliedTo, notify]);

  useEffect(() => { loadData(1); }, [loadData]);


  useEffect(() => {
    const handler = e => {
      if (dateFilterRef.current && !dateFilterRef.current.contains(e.target)) setShowDateFilter(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const submitSearch = (value) => setAppliedSearch((value ?? '').trim());

  const applyDateFilter = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
    setShowDateFilter(false);
  };

  const clearDateFilter = () => {
    setDateFrom(''); setDateTo('');
    setAppliedFrom(''); setAppliedTo('');
    setShowDateFilter(false);
  };

  // The nav item for this page clicked while on it is a new history entry: start clean. The page
  // keeps no memory, so Back does not restore a previous search here either.
  const location = useLocation();
  const entryKeyRef = useRef(location.key);
  useEffect(() => {
    if (entryKeyRef.current === location.key) return;
    entryKeyRef.current = location.key;
    setSearchTerm(''); setAppliedSearch('');
    setDateFrom(''); setDateTo(''); setAppliedFrom(''); setAppliedTo('');
    setShowDateFilter(false);
  }, [location.key]);

  const toggleBatch = async (batchId) => {
    const current = expanded[batchId];
    if (current) {
      setExpanded(prev => {
        const next = { ...prev };
        delete next[batchId];
        return next;
      });
      return;
    }

    setExpanded(prev => ({ ...prev, [batchId]: { loading: true, rows: [] } }));
    try {
      const res = await adminAuditApi.getBatch(batchId);
      setExpanded(prev => ({ ...prev, [batchId]: { loading: false, rows: res.data?.rows || [] } }));
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to load batch detail');
      setExpanded(prev => {
        const next = { ...prev };
        delete next[batchId];
        return next;
      });
    }
  };

  /**
   * Fetch the flat field rows for the CURRENT FILTERS, then open the modal.
   *
   * DownloadModal takes `data` as a plain prop and closes over it, so the rows have
   * to exist before it opens. They are fetched rather than scraped from `expanded`:
   * the batch list is collapsed by default, so an export built from expanded state
   * produced an empty CSV for anyone who just clicked download — which is the
   * normal thing to do.
   */
  const openDownload = async () => {
    setPreparingDownload(true);
    try {
      const params = { limit: 20000 };
      if (appliedSearch) params.search    = canonicalQuery(appliedSearch, SEARCH_FIELDS, SEARCH_FIELD_LABELS);
      const range = localDaysToUtcRange(appliedFrom, appliedTo);
      if (range.from) params.dateFrom = range.from;
      if (range.to)   params.dateTo   = range.to;

      const res = await adminAuditApi.getRows(params);
      const rows = res.data?.rows || [];

      if (rows.length === 0) {
        notify.warning('Nothing to export for the current filters.');
        return;
      }
      // Never hand over a truncated audit trail without saying so.
      if (res.data?.truncated) {
        notify.warning(
          `Export capped at ${res.data.returned.toLocaleString()} of ` +
          `${res.data.totalCount.toLocaleString()} rows — narrow the filters for the rest.`);
      }

      setDownloadRows(rows.map(r => ({ ...r, changedAt: formatTimestamp(r.changedAt) || '—' })));
      setShowDownload(true);
    } catch (err) {
      notify.error(err.response?.data?.error || 'Failed to prepare the export');
    } finally {
      setPreparingDownload(false);
    }
  };

  return (
    <div className="stui-module-container">

      {/* Header */}
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <h1>Audit Trail - Admin</h1>
          <span className="stui-module-header-meta">
            {loading ? '' : `${totalCount.toLocaleString()} action${totalCount !== 1 ? 's' : ''}`}
          </span>
        </div>

        <div className="stui-module-header-actions">

          {/* Date filter */}
          <div className="stui-popover-anchor" ref={dateFilterRef}>
            <button
              className={`stui-crud-btn${dateFilterActive ? ' active' : ''}`}
              onClick={() => setShowDateFilter(v => !v)}
              title={dateFilterActive ? `Filtered: ${appliedFrom || '…'} – ${appliedTo || '…'}` : 'Filter by date'}
            ><Calendar size={17} /></button>
            {showDateFilter && (
              <div className="stui-popover">
                <div className="stui-field-group">
                  <label>From</label>
                  <StuiDateTime mode="date" valueZone="none" value={dateFrom} onChange={setDateFrom} />
                </div>
                <div className="stui-field-group">
                  <label>To</label>
                  <StuiDateTime mode="date" valueZone="none" value={dateTo} onChange={setDateTo} />
                </div>
                <div className="stui-popover-footer">
                  {(dateFrom || dateTo || appliedFrom || appliedTo) && (
                    <button className="stui-btn stui-btn-secondary" onClick={clearDateFilter}>Clear</button>
                  )}
                  <button className="stui-btn stui-btn-primary" onClick={applyDateFilter}>Apply</button>
                </div>
              </div>
            )}
          </div>

          {/* Search */}
          <div className="stui-search-wrapper">
            <SearchClearIcon searchTerm={searchTerm} onClear={() => { setSearchTerm(''); submitSearch(''); }} />
            <input
              type="text"
              className="stui-search-input"
              placeholder="Search audit trail (press Enter)..."
              title={SEARCH_HINT}
              value={searchTerm}
              onChange={e => {
                setSearchTerm(e.target.value);
                if (!e.target.value) submitSearch('');
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); submitSearch(e.target.value); }
              }}
            />
          </div>

          <ActionMenu
            items={[{ key: 'adv-search', icon: <ListFilter size={16} />, label: 'Advanced Search…', onClick: () => setShowAdvSearch(true) }]}
            disabled={preparingDownload}
            onDownload={openDownload}
            downloadLabel="Export…"
            showPurge
            onPurge={() => setShowPurge(true)}
          />
        </div>
      </div>


      {/* Batch list */}
      <div className="stui-datasheet-view">
        <div className="stui-data-grid-container">
          <div className="stui-data-grid-scroll-area">
          <table className="stui-data-grid">
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th style={{ width: 180 }}>When</th>
                <th style={{ width: 200 }}>Who</th>
                <th>Action</th>
                <th style={{ width: 220 }}>Affected</th>
                <th style={{ width: 90, textAlign: 'right' }}>Changes</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="stui-td-empty">
                  Loading...
                </td></tr>
              )}

              {!loading && batches.length === 0 && (
                <tr><td colSpan={6} className="stui-td-empty">
                  No admin changes recorded{filtersActive ? ' for these filters' : ''}.
                </td></tr>
              )}

              {!loading && batches.map(b => {
                const exp = expanded[b.batchId];
                const isOpen = !!exp;
                return (
                  <React.Fragment key={b.batchId}>
                    <tr
                      onClick={() => toggleBatch(b.batchId)}
                      style={{ cursor: 'pointer', background: isOpen ? 'var(--color-bg-subtle)' : undefined }}
                    >
                      <td className="stui-td-center stui-text-muted">
                        {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                      </td>
                      <td className="stui-text-nowrap">{formatTimestamp(b.changedAt) || '—'}</td>
                      <td className="stui-truncate" title={b.changedBy}>{b.changedBy}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', flexWrap: 'wrap' }}>
                          <span className="stui-fw-medium">{b.operation || '(unlabelled)'}</span>
                          {(b.categories || []).map(c => (
                            <span key={c} className={auditBadge(c)}>{c}</span>
                          ))}
                          {(b.actions || []).map(a => (
                            <span key={a} className={auditBadge(a)}>{a}</span>
                          ))}
                        </div>
                      </td>
                      <td className="stui-truncate" title={((b.subjects || []).length > 0 ? b.subjects : (b.tables || [])).join(', ')}>
                        {(b.subjects || []).length > 0
                          ? b.subjects.join(', ')
                          : (b.tables || []).join(', ')}
                      </td>
                      <td className="stui-cell-right" style={{ fontVariantNumeric: 'tabular-nums' }}>{b.rowCount}</td>
                    </tr>

                    {isOpen && (
                      <tr>
                        <td colSpan={6} style={{ padding: 0, background: 'var(--color-bg-subtle)' }}>
                          {exp.loading ? (
                            <div style={{ padding: 'var(--spacing-md) 44px', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                              Loading changes...
                            </div>
                          ) : (
                            <table className="stui-inner-table">
                              <thead>
                                <tr>
                                  <th style={{ width: 32 }}></th>
                                  <th style={{ width: 180 }}>Table</th>
                                  <th style={{ width: 60 }}>ID</th>
                                  <th style={{ width: 180 }}>Record</th>
                                  <th style={{ width: 80 }}>Action</th>
                                  <th style={{ width: 150 }}>Field</th>
                                  <th>Old</th>
                                  <th>New</th>
                                </tr>
                              </thead>
                              <tbody>
                                {exp.rows.map(r => (
                                  <tr key={r.id}>
                                    <td></td>
                                    <td className="stui-td-xs">{r.tableName}</td>
                                    <td className="stui-td-xs">{r.recordPk ?? '—'}</td>
                                    <td className="stui-td-xs">{r.recordIdentifier || '—'}</td>
                                    <td>
                                      <span className={auditBadge(r.action)}>{r.action}</span>
                                    </td>
                                    <td className="stui-td-xs">{r.fieldName || <span className="stui-text-muted">(snapshot)</span>}</td>
                                    <td><ValueCell value={r.oldValue} /></td>
                                    <td><ValueCell value={r.newValue} /></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>

        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          rowCount={batches.length}
          loading={loading}
          onPage={loadData}
          onPageSize={ps => setPageSize(ps)}
        />
      </div>

      {showPurge && (
        <div className="stui-modal-overlay">
          <div className="stui-modal stui-modal-md">
            <div className="stui-modal-header">
              <h3 className="stui-modal-title">Purge Old Records</h3>
              <button className="stui-modal-close" onClick={closePurge}><X size={18} /></button>
            </div>
            <div className="stui-modal-body">
              <p className="stui-hint">
                Applies the retention policy to this trail (AudTrl_AdminCRUD), per category.
                There is no undo and no recycle bin. Retention is set on
                Administration &rsaquo; Security &rsaquo; Preference Defaults (System tab).
              </p>

              <div className="stui-form-row-inline">
                <label className="stui-checkbox">
                  <input
                    className="stui-checkbox-input"
                    type="checkbox"
                    checked={useOverride}
                    onChange={e => {
                      setUseOverride(e.target.checked);
                      setPurgePreview(null);
                      setPreviewedFor(null);
                    }}
                  />
                  <span>Override for this run only — not saved to policy</span>
                </label>
              </div>

              {useOverride && (
                <div className="stui-form-row-inline">
                  <label className="stui-label">Delete records older than</label>
                  <input
                    type="number"
                    min="0"
                    className="stui-field-input stui-inner-table-input-sm"
                    value={overrideDays}
                    onChange={e => {
                      setOverrideDays(e.target.value);
                      setPurgePreview(null);
                      setPreviewedFor(null);
                    }}
                    placeholder="days"
                  />
                  <span className="stui-label">days (0 = never)</span>
                </div>
              )}

              <div className="stui-form-row-inline">
                <button
                  className="stui-btn stui-btn-secondary stui-btn-sm"
                  onClick={runPurgePreview}
                  disabled={previewing}
                >
                  {previewing ? 'Checking...' : 'Preview'}
                </button>
              </div>

              {purgePreview && previewedFor === previewKey() && (
                <>
                  <div className="stui-detail-section-header">
                    Would delete {purgePreview.wasOverride ? '(override)' : '(policy)'}
                  </div>
                  <table className="stui-inner-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Keep (days)</th>
                        <th className="stui-cell-right">Rows</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purgePreview.categories.map(c => (
                        <tr key={c.category}>
                          <td>{c.category}</td>
                          <td className={c.never ? 'stui-td-muted' : ''}>
                            {c.never ? 'never' : `${c.days}`}
                          </td>
                          <td className={`stui-cell-right ${c.rows > 0 ? 'stui-fw-semibold' : 'stui-td-muted'}`}>
                            {c.rows}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td className="stui-fw-semibold">Total</td>
                        <td></td>
                        <td className="stui-cell-right stui-fw-semibold">{purgePreview.totalRows}</td>
                      </tr>
                    </tbody>
                  </table>
                  {purgePreview.totalRows === 0 && (
                    <p className="stui-hint">Nothing is outside the retention window — there is nothing to purge.</p>
                  )}
                </>
              )}
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={closePurge}>Cancel</button>
              <button
                className="stui-btn stui-btn-danger"
                onClick={doPurge}
                disabled={purging || previewedFor !== previewKey() || !purgePreview?.totalRows}
              >
                {purging ? 'Purging...' : 'Purge'}
              </button>
            </div>
          </div>
        </div>
      )}

      <DownloadModal
        isOpen={showDownload}
        onClose={() => setShowDownload(false)}
        data={downloadRows}
        columns={DOWNLOAD_COLUMNS}
        tableName="AudTrl_AdminCRUD"
      />
      <AdvancedSearchModal
        isOpen={showAdvSearch}
        onClose={() => setShowAdvSearch(false)}
        query={searchTerm}
        fields={SEARCH_FIELD_LABELS}
        onApply={q => { setSearchTerm(q); submitSearch(q); }}
      />
    </div>
  );
};

/**
 * CREATE/DELETE store a JSON snapshot in a single cell. Pretty-printing it inline
 * would blow the row height apart, so it collapses to a summary that expands on
 * click.
 */
const ValueCell = ({ value }) => {
  const [open, setOpen] = useState(false);

  if (value == null || value === '') {
    return <span className="stui-text-muted stui-text-xs">—</span>;
  }

  const looksLikeJson = value.length > 60 && value.trim().startsWith('{');
  if (!looksLikeJson) {
    return <span className="stui-text-xs stui-break-word">{value}</span>;
  }

  let pretty = value;
  let fieldCount = null;
  try {
    const parsed = JSON.parse(value);
    pretty = JSON.stringify(parsed, null, 2);
    fieldCount = Object.keys(parsed).length;
  } catch {
    // Not valid JSON after all — fall through and show the raw text.
  }

  return (
    <div>
      <button
        className="stui-btn stui-btn-secondary"
        style={{ fontSize: 'var(--text-xs)', padding: '1px var(--spacing-sm)' }}
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
      >
        {open ? 'Hide' : `Snapshot${fieldCount != null ? ` (${fieldCount} fields)` : ''}`}
      </button>
      {open && (
        <pre style={{
          margin: 'var(--spacing-sm) 0 0', fontSize: 'var(--text-xs)', lineHeight: 1.4, maxHeight: 260,
          overflow: 'auto', background: 'var(--color-bg-white)',
          border: 'var(--border-width) solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: 'var(--spacing-sm)',
        }}>{pretty}</pre>
      )}
    </div>
  );
};

const Pagination = ({ page, totalPages, totalCount, pageSize, rowCount, loading, onPage, onPageSize }) => (
  <div className="stui-pagination">
    <div className="stui-pagination-info">
      <span>Showing {rowCount.toLocaleString()} of {totalCount.toLocaleString()} actions</span>
    </div>
    <div className="stui-pagination-controls">
      <label className="stui-pagination-pagesize">
        <span>Per page:</span>
        <StuiSelect value={pageSize} onChange={e => { onPageSize(Number(e.target.value)); onPage(1); }}>
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </StuiSelect>
      </label>
      <div className="stui-pagination-nav">
        <button className="stui-pagination-btn" onClick={() => onPage(1)} disabled={page <= 1 || loading} title="First page"><ChevronFirst size={18} /></button>
        <button className="stui-pagination-btn" onClick={() => onPage(page - 1)} disabled={page <= 1 || loading} title="Previous page"><ChevronLeft size={18} /></button>
        <span className="stui-pagination-page-info">Page {page} of {totalPages}</span>
        <button className="stui-pagination-btn" onClick={() => onPage(page + 1)} disabled={page >= totalPages || loading} title="Next page"><ChevronRight size={18} /></button>
        <button className="stui-pagination-btn" onClick={() => onPage(totalPages)} disabled={page >= totalPages || loading} title="Last page"><ChevronLast size={18} /></button>
      </div>
    </div>
  </div>
);

export default AuditTrailAdminPage;
